import React, { useEffect, useRef, useState, useCallback, useMemo} from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../supabaseClient';
import logo from '../assets/images/logo.png';

const CANVAS_W = 580;
const CANVAS_H = 480;
const PLAYER_R = 19;

const ARROW_COLOR = '#facc15';
const DRIBBLE_COLOR = '#4ade80';
const CUT_COLOR = '#e0f2fe';
const DRAW_COLOR = '#fb923c';

const DEFAULT_POSITIONS = [
    { x: 290, y: 120 }, // Point Guard (Top of key)
    { x: 120, y: 220 }, // Left Wing
    { x: 460, y: 220 }, // Right Wing
    { x: 50, y: 380 }, // Left Corner 3
    { x: 530, y: 380 }, // Right Corner 3
];

export default function Clipboard() {
    const { user } = useAuth();
    const { isDark: darkMode } = useTheme();

    const canvasRef = useRef(null);

    const [selectedTeamId, setSelectedTeamId] = useState(user?.team_id || '');
    const [teams, setTeams] = useState([]);
    const [allPlayers, setAllPlayers] = useState([]);     // Full roster from DB
    const [courtPlayers, setCourtPlayers] = useState([]); // Players currently on canvas

    const logoImg = useMemo(() => {
        const img = new Image();
        img.src = logo;
        return img; 
    }, []);

    const [defense, setDefense] = useState([
        { x: 290, y: 190, jersey: "X1" }, // Defending PG
        { x: 160, y: 270, jersey: "X2" }, // Defending Left Wing
        { x: 420, y: 270, jersey: "X3" }, // Defending Right Wing
        { x: 140, y: 370, jersey: "X4" }, // Defending Left Corner
        { x: 440, y: 370, jersey: "X5" }, // Defending Right Corner
    ]);
    const [showDefense] = useState(true); // kept because it's used in render + toggle

    const [arrows, setArrows] = useState([]);
    const [dribbles, setDribbles] = useState([]);
    const [cuts, setCuts] = useState([]);
    const [screens, setScreens] = useState([]);
    const [strokes, setStrokes] = useState([]);

    const [tool] = useState('move'); // kept because it's used in pointer handlers
    const [ballHandlerIndex, setBallHandlerIndex] = useState(0);

    // Refs for smooth interaction
    const stateRef = useRef({});
    const draggingRef = useRef(null); // { type: 'court' | 'defense', index, offsetX, offsetY }
    const arrowStartRef = useRef(null);
    const liveArrowRef = useRef(null);
    const freeStrokeRef = useRef(null);

    stateRef.current = { courtPlayers, defense, arrows, dribbles, cuts, screens, strokes, tool, ballHandlerIndex, showDefense };

    // Load teams for dropdown
    useEffect(() => {
        async function loadTeams() {
            const { data } = await supabase.from('teams').select('id, name').is('archived_at',null).order('name');
            setTeams(data || []);
        }
        loadTeams();
    }, []);

    // Load full roster when team changes
    useEffect(() => {
        if (!selectedTeamId) {
            setAllPlayers([]);
            setCourtPlayers([]);
            return;
        }

        async function loadRoster() {
            const { data } = await supabase
                .from('players')
                .select('id, name, jersey_number')
                .eq('team_id', selectedTeamId)
                .order('jersey_number');

            const formatted = (data || []).map((p, idx) => ({
                id: p.id,
                jersey: p.jersey_number || idx + 1,
                name: p.name.split(' ').pop().slice(0, 9).toUpperCase(),
                fullName: p.name,
            }));

            setAllPlayers(formatted);

            // Auto-place first 5 on court
            const initialCourt = formatted.slice(0, 5).map((p, i) => ({
                ...p,
                x: DEFAULT_POSITIONS[i]?.x || 200 + i * 80,
                y: DEFAULT_POSITIONS[i]?.y || 250,
            }));
            setCourtPlayers(initialCourt);
            setBallHandlerIndex(0);
        }

        loadRoster();
    }, [selectedTeamId]);

    // ── Half-Court ───────────────────────
    const drawHalfCourt = useCallback((ctx) => {
        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H); // Wipe the old frame clean
        ctx.globalAlpha = 1.0 // Reset transparency for floor/lines
        const W = CANVAS_W;
        const H = CANVAS_H;
        const cx = W / 2;
        const baselineY = H - 42;

        // Wooden floor
        ctx.fillStyle = darkMode ? '#92400e' : '#b45309';
        ctx.fillRect(0, 0, W, H);

        // Subtle wood grain
        ctx.strokeStyle = darkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.13)';
        ctx.lineWidth = 1.5;
        for (let i = 35; i < W - 35; i += 26) {
            ctx.beginPath();
            ctx.moveTo(i, 35);
            ctx.lineTo(i, H - 72);
            ctx.stroke();
        }

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3.5;

        // Outer boundary
        ctx.strokeRect(32, 32, W - 64, H - 74);

        // Baseline
        ctx.beginPath();
        ctx.moveTo(32, baselineY);
        ctx.lineTo(W - 32, baselineY);
        ctx.stroke();

        // Key / Paint
        const keyW = 192;
        const keyH = 172;
        const keyX = cx - keyW / 2;
        ctx.strokeRect(keyX, baselineY - keyH, keyW, keyH);

        // Free-throw lane
        const laneW = 122;
        const laneX = cx - laneW / 2;
        ctx.strokeRect(laneX, baselineY - keyH, laneW, keyH);

        // Free-throw line
        ctx.beginPath();
        ctx.moveTo(laneX, baselineY - keyH);
        ctx.lineTo(laneX + laneW, baselineY - keyH);
        ctx.stroke();

        // Free-throw circle (top half)
        ctx.beginPath();
        ctx.arc(cx, baselineY - keyH, laneW / 2, 0, Math.PI, true);
        ctx.stroke();

        // ── Hoop & Backboard (Fixed: Facing the court) ───────────────────────
        const rimY = baselineY - 29;

        // 1. Backboard (Draw first so it's behind the rim)
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(cx - 35, baselineY - 8); // Moved closer to baseline
        ctx.lineTo(cx + 35, baselineY - 8);
        ctx.stroke();

        // 2. Rim Support (Connects backboard to rim)
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(cx, baselineY - 8);
        ctx.lineTo(cx, rimY+11);
        ctx.stroke();

        // 3. Rim (Drawn last to sit "on top" of support)
        ctx.strokeStyle = '#ff6600';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(cx, rimY, 12, 0, Math.PI * 2);
        ctx.stroke();

        // Reset stroke for remaining lines
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3.5;

        // --- 3-Point Line Logic ---
        const threeRadius = 212; // Adjusted for your canvas scale
        const cornerDist = 211;  // Distance from center to side-line (straight part)

        // Calculate the "break" angle where the straight line meets the arc
        // Math.acos(adjacent / hypotenuse)
        const breakAngle = Math.acos(cornerDist / threeRadius);

        ctx.beginPath();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3.5;

        // 1. Left Corner (Straight line from baseline up to the break point)
        const leftX = cx - cornerDist;
        const breakY = rimY - (threeRadius * Math.sin(breakAngle));
        ctx.moveTo(leftX, baselineY);
        ctx.lineTo(leftX, breakY);

        // 2. The Arc
        // Note: Canvas angles start at 3 o'clock (0). 
        // We go from (PI + breakAngle) to (2*PI - breakAngle) to draw the top arc.
        ctx.arc(cx, rimY, threeRadius, Math.PI + breakAngle, -breakAngle, false);

        // 3. Right Corner (Straight line from break point back to baseline)
        const rightX = cx + cornerDist;
        ctx.lineTo(rightX, baselineY);

        ctx.stroke();

        // ── Faint Watermark "Muqalab Hoops" + Logo ─────────────────────────────
        ctx.save();
        ctx.globalAlpha = 0.12;                    // very faint
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 42px system-ui';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('MUQALAB HOOPS', cx, H / 2 - 12);

        // ── Logo Watermark ─────────────────────────────
        ctx.save();
        ctx.globalAlpha = 0.35; // Makes it a faint watermark

        // Calculate center position
        const logoSize = 128; // Set desired width/height
        const logoX = cx - logoSize / 2 ;
        const logoY = H / 2 - logoSize / 2 - 150;

        // Only draw if the image has finished loading
        if (logoImg.complete) {
            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
        } else {
            logoImg.onload = () => {
                // This triggers a redraw if the image wasn't ready yet
            };
        }

        ctx.restore();

    }, [darkMode, logoImg]);

    // ── Drawing Helpers ─────────────────────────────────────────────────────
    function drawArrowLine(ctx, x1, y1, x2, y2, color = ARROW_COLOR, alpha = 1) {
        const dx = x2 - x1, dy = y2 - y1;
        if (Math.hypot(dx, dy) < 8) return;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.8;
        ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();

        const ang = Math.atan2(dy, dx);
        const len = 14;
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - len * Math.cos(ang - 0.45), y2 - len * Math.sin(ang - 0.45));
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - len * Math.cos(ang + 0.45), y2 - len * Math.sin(ang + 0.45));
        ctx.stroke();
        ctx.restore();
    }

    function drawFreehandArrow(ctx, pts, color, dashed = false) {
        if (pts.length < 2) return;
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.8;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        if (dashed) ctx.setLineDash([7, 4]);
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.stroke();
        ctx.setLineDash([]);

        const last = pts[pts.length - 1];
        const prev = pts[pts.length - 2];
        const ang = Math.atan2(last.y - prev.y, last.x - prev.x);
        const len = 13;
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(last.x - len * Math.cos(ang - 0.45), last.y - len * Math.sin(ang - 0.45));
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(last.x - len * Math.cos(ang + 0.45), last.y - len * Math.sin(ang + 0.45));
        ctx.stroke();
        ctx.restore();
    }

    function drawFreeStroke(ctx, pts, color = DRAW_COLOR) {
        if (pts.length < 2) return;
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.8;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.stroke();
        ctx.restore();
    }

    function drawScreen(ctx, s) {
        const BAR = 22;
        const ang = s.angle || 0;
        const px = Math.cos(ang + Math.PI / 2) * BAR;
        const py = Math.sin(ang + Math.PI / 2) * BAR;
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 7;
        ctx.beginPath();
        ctx.moveTo(s.x - px, s.y - py);
        ctx.lineTo(s.x + px, s.y + py);
        ctx.stroke();
        ctx.restore();
    }

    function drawPlayerToken(ctx, p, isBallHandler = false) {
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 9;
        ctx.shadowOffsetY = 3;
        ctx.beginPath();
        ctx.arc(p.x, p.y, PLAYER_R, 0, Math.PI * 2);
        ctx.fillStyle = '#1e40af';
        ctx.fill();
        ctx.restore();

        ctx.beginPath();
        ctx.arc(p.x, p.y, PLAYER_R, 0, Math.PI * 2);
        ctx.strokeStyle = isBallHandler ? '#facc15' : '#ffffff';
        ctx.lineWidth = isBallHandler ? 5.5 : 2.5;
        ctx.stroke();

        if (isBallHandler) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, PLAYER_R + 11, 0, Math.PI * 2);
            ctx.strokeStyle = '#facc15';
            ctx.lineWidth = 2.5;
            ctx.globalAlpha = 0.6;
            ctx.stroke();
            ctx.globalAlpha = 1;
        }

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 14px system-ui';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(p.jersey, p.x, p.y);

        ctx.font = '9px system-ui';
        ctx.textBaseline = 'top';
        ctx.fillText(p.name || '', p.x, p.y + PLAYER_R + 7);
    }

    // ── Render Loop ─────────────────────────────────────────────────────────
    const render = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.globalAlpha = 1.0 // Reset transparency for floor/lines


        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
        drawHalfCourt(ctx);

        if (showDefense) {
            defense.forEach(p => {
                ctx.globalAlpha = 1.0 // Reset transparency for floor/lines
                ctx.fillStyle = darkMode ? '#4b5563' : '#6b7280';
                ctx.beginPath();
                ctx.arc(p.x, p.y, PLAYER_R, 0, Math.PI * 2);
                ctx.fill();
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 2.5;
                ctx.stroke();

                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 13px system-ui';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(p.jersey, p.x, p.y);
            });
        }

        // Rest of your drawings (strokes, dribbles, cuts, arrows, screens, players)
        strokes.forEach(pts => drawFreeStroke(ctx, pts));
        if (tool === 'draw' && freeStrokeRef.current) drawFreeStroke(ctx, freeStrokeRef.current);

        dribbles.forEach(pts => drawFreehandArrow(ctx, pts, DRIBBLE_COLOR, true));
        if (tool === 'dribble' && freeStrokeRef.current) drawFreehandArrow(ctx, freeStrokeRef.current, DRIBBLE_COLOR, true);

        cuts.forEach(pts => drawFreehandArrow(ctx, pts, CUT_COLOR));
        if (tool === 'cut' && freeStrokeRef.current) drawFreehandArrow(ctx, freeStrokeRef.current, CUT_COLOR);

        arrows.forEach(a => drawArrowLine(ctx, a.x1, a.y1, a.x2, a.y2));
        if (liveArrowRef.current) {
            const a = liveArrowRef.current;
            drawArrowLine(ctx, a.x1, a.y1, a.x2, a.y2, ARROW_COLOR, 0.65);
        }

        screens.forEach(s => drawScreen(ctx, s));

        courtPlayers.forEach((p, i) => drawPlayerToken(ctx, p, i === ballHandlerIndex));
    }, [
        drawHalfCourt,          
        showDefense,
        defense,
        darkMode,
        strokes,
        tool,
        freeStrokeRef,          // ref is stable
        dribbles,
        cuts,
        arrows,
        liveArrowRef,
        screens,
        courtPlayers,
        ballHandlerIndex,
        // Add drawFreeStroke, drawFreehandArrow, etc. if they are defined inside component
    ]);

    useEffect(() => { render(); }, [render]);

    // ── Pointer Handlers ─────────────────────────────────────────────────────
    const getPos = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const scaleX = CANVAS_W / rect.width;
        const scaleY = CANVAS_H / rect.height;
        const client = e.touches ? e.touches[0] : e;
        return {
            x: (client.clientX - rect.left) * scaleX,
            y: (client.clientY - rect.top) * scaleY,
        };
    };

    const hitPlayer = (x, y, list) => {
        for (let i = list.length - 1; i >= 0; i--) {
            if (Math.hypot(list[i].x - x, list[i].y - y) <= PLAYER_R + 6) return i;
        }
        return -1;
    };

    const onPointerDown = (e) => {
        const pos = getPos(e);
        const s = stateRef.current;

        if (tool === 'move') {
            let idx = hitPlayer(pos.x, pos.y, s.courtPlayers);
            if (idx >= 0) {
                draggingRef.current = { type: 'court', index: idx, offsetX: pos.x - s.courtPlayers[idx].x, offsetY: pos.y - s.courtPlayers[idx].y };
                return;
            }
            idx = hitPlayer(pos.x, pos.y, s.defense);
            if (idx >= 0 && showDefense) {
                draggingRef.current = { type: 'defense', index: idx, offsetX: pos.x - s.defense[idx].x, offsetY: pos.y - s.defense[idx].y };
            }
        } else if (tool === 'arrow') {
            arrowStartRef.current = pos;
            liveArrowRef.current = { x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y };
        } else if (['dribble', 'cut', 'draw'].includes(tool)) {
            freeStrokeRef.current = [pos];
        } else if (tool === 'screen') {
            setScreens(prev => [...prev, { x: pos.x, y: pos.y, angle: 0 }]);
        }
    };

    const onPointerMove = (e) => {
        const pos = getPos(e);
        const s = stateRef.current;

        if (tool === 'move' && draggingRef.current) {
            const { type, index, offsetX, offsetY } = draggingRef.current;
            if (type === 'court') {
                const next = [...s.courtPlayers];
                next[index] = { ...next[index], x: pos.x - offsetX, y: pos.y - offsetY };
                setCourtPlayers(next);
            } else if (type === 'defense') {
                const next = [...s.defense];
                next[index] = { ...next[index], x: pos.x - offsetX, y: pos.y - offsetY };
                setDefense(next);
            }
            render();
        } else if (tool === 'arrow' && arrowStartRef.current) {
            liveArrowRef.current = { x1: arrowStartRef.current.x, y1: arrowStartRef.current.y, x2: pos.x, y2: pos.y };
            render();
        } else if (['dribble', 'cut', 'draw'].includes(tool) && freeStrokeRef.current) {
            freeStrokeRef.current.push(pos);
            render();
        }
    };

    const onPointerUp = () => {
        if (tool === 'move') {
            draggingRef.current = null;
        } else if (tool === 'arrow' && liveArrowRef.current) {
            const a = liveArrowRef.current;
            if (Math.hypot(a.x2 - a.x1, a.y2 - a.y1) > 12) {
                setArrows(prev => [...prev, { ...a }]);
            }
            liveArrowRef.current = null;
            arrowStartRef.current = null;
        } else if (['dribble', 'cut', 'draw'].includes(tool) && freeStrokeRef.current?.length > 3) {
            const pts = freeStrokeRef.current;
            if (tool === 'dribble') setDribbles(prev => [...prev, pts]);
            else if (tool === 'cut') setCuts(prev => [...prev, pts]);
            else setStrokes(prev => [...prev, pts]);
        }
        freeStrokeRef.current = null;
        render();
    };

    // Add player from roster to court
    const addPlayerToCourt = (playerFromRoster) => {
        if (courtPlayers.length >= 7) return;
        const newPlayer = {
            ...playerFromRoster,
            x: DEFAULT_POSITIONS[courtPlayers.length % DEFAULT_POSITIONS.length].x,
            y: DEFAULT_POSITIONS[courtPlayers.length % DEFAULT_POSITIONS.length].y,
        };
        setCourtPlayers(prev => [...prev, newPlayer]);
    };

    const removeFromCourt = (courtIndex) => {
        setCourtPlayers(prev => prev.filter((_, i) => i !== courtIndex));
        if (ballHandlerIndex >= courtPlayers.length - 1 && courtPlayers.length > 1) {
            setBallHandlerIndex(0);
        }
    };

    const exportPNG = () => {
        const link = document.createElement('a');
        link.download = `Muqalab_Play_${new Date().toISOString().slice(0, 10)}.png`;
        link.href = canvasRef.current.toDataURL('image/png');
        link.click();
    };

    return (
        <div className={`min-h-screen ${darkMode ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'} p-4 md:p-8`}>
            <div className="max-w-5xl mx-auto">
                <h1 className="text-3xl font-bold mb-1 flex items-center gap-3">📋 Coach's Clipboard</h1>
                <p className="text-gray-500 dark:text-gray-400 mb-6">Full roster • Drag players • Design plays</p>

                <select
                    value={selectedTeamId}
                    onChange={e => setSelectedTeamId(e.target.value)}
                    className="w-full md:w-96 mb-6 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl px-4 py-3 text-lg font-medium"
                >
                    <option value="">— Select a team —</option>
                    {teams.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>

                <div className="relative rounded-3xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-2xl bg-black mb-8">
                    <canvas
                        ref={canvasRef}
                        width={CANVAS_W}
                        height={CANVAS_H}
                        className="w-full block touch-none"
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerLeave={onPointerUp}
                    />
                </div>

                {allPlayers.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-8">
                        <h3 className="font-semibold mb-4">Full Roster — Click to add to court</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {allPlayers.map((player) => {
                                const isOnCourt = courtPlayers.some(cp => cp.id === player.id);
                                return (
                                    <button
                                        key={player.id}
                                        onClick={() => !isOnCourt && addPlayerToCourt(player)}
                                        disabled={isOnCourt}
                                        className={`p-4 rounded-2xl flex items-center gap-3 text-left transition-all border
                      ${isOnCourt
                                                ? 'bg-green-100 dark:bg-green-900/30 border-green-400 opacity-75 cursor-default'
                                                : 'bg-gray-100 dark:bg-gray-700 hover:bg-orange-50 dark:hover:bg-orange-900/30 border-transparent hover:border-orange-300'}`}
                                    >
                                        <div className="w-9 h-9 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold shrink-0">
                                            {player.jersey}
                                        </div>
                                        <div className="truncate">
                                            <div className="font-medium">{player.fullName}</div>
                                            <div className="text-xs text-gray-500">#{player.jersey}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {courtPlayers.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-semibold">On Court ({courtPlayers.length})</h3>
                            <button
                                onClick={() => setCourtPlayers([])}
                                className="text-red-600 dark:text-red-400 text-sm hover:underline"
                            >
                                Clear Court
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-3 mb-8">
                            {courtPlayers.map((p, i) => (
                                <div key={p.id || i} className="group relative bg-blue-600 text-white px-5 py-3 rounded-2xl flex items-center gap-3">
                                    <div className="w-8 h-8 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold">
                                        {p.jersey}
                                    </div>
                                    <div className="pr-6">
                                        {p.fullName || p.name}
                                    </div>
                                    <button
                                        onClick={() => removeFromCourt(i)}
                                        className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div>
                            <p className="font-medium mb-3">Ball Handler (yellow highlight)</p>
                            <div className="flex flex-wrap gap-3">
                                {courtPlayers.map((p, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setBallHandlerIndex(i)}
                                        className={`px-6 py-3 rounded-2xl flex items-center gap-3 font-medium transition ${ballHandlerIndex === i ? 'bg-yellow-400 text-black shadow' : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        <span className="bg-blue-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold">
                                            {p.jersey}
                                        </span>
                                        {p.fullName || p.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-center mt-8">
                    <button
                        onClick={exportPNG}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-2xl font-medium flex items-center gap-2"
                    >
                        📸 Export Play as PNG
                    </button>
                </div>
            </div>
        </div>
    );
}